/**
 * Was this order paid for at some point?
 *
 * A refunded or part-refunded order is still an order the customer paid for.
 * They keep their invoice, their purchase history, and -- because refunds are
 * issued per pin -- usually pins they never got money back for.
 *
 * Screens must therefore ask "was this paid?" rather than "is the status
 * exactly 'paid'?" before showing pins, invoices or reviews. Asking the latter
 * hides things the customer still owns the moment any refund completes.
 *
 * The individual pins are filtered on their own refund state by the API
 * (OrdersService.getOrderItemPins), so nothing refunded leaks through here.
 */
export const PAID_OR_REFUNDED = ["paid", "refunded", "partial_refund"];

export const wasPaid = (paymentStatus?: string | null): boolean =>
  PAID_OR_REFUNDED.includes(String(paymentStatus || ""));

/** True only while payment is genuinely still outstanding (so "retry payment"
 *  is never offered on an order that was paid and later refunded). */
export const awaitingPayment = (paymentStatus?: string | null): boolean =>
  !wasPaid(paymentStatus);

/**
 * Does this order contain a travel booking?
 *
 * Travel is the one product where paying late is worse than not paying at
 * all. FlixBus holds the chosen seats for about ten minutes and releases
 * them when that lapses, so by the time an unpaid booking is sitting in My
 * Orders the seats are already gone. Taking the money then would charge the
 * customer for a reservation that no longer exists.
 *
 * So travel never offers "retry payment" -- the customer is asked to search
 * and book again. The API refuses a lapsed travel payment as well
 * (blockIfTravelHoldLapsed); this is what stops them being invited to try in
 * the first place, rather than clicking and meeting an error.
 *
 * Derived here, in one place, because every screen that decides whether to
 * offer payment has to reach the same answer.
 */
export const isTravelOrder = (orderItems?: any[] | null): boolean =>
  Array.isArray(orderItems) &&
  orderItems.some(
    (item: any) => String(item?.productType || "").toLowerCase() === "travel",
  );

/**
 * Is there anything on this order that is still worth paying for?
 *
 * A customer may buy a gift card or a top-up alongside a bus ticket. If the
 * seat hold lapses the server drops the ticket and recalculates the total,
 * but the rest of the order is untouched and perfectly payable -- so the
 * retry has to survive for it.
 *
 * This is why the travel test is not enough on its own. Suppressing the
 * retry whenever an order merely CONTAINS travel killed the gift card too:
 * nothing was wrong with it, and no screen offered any way to pay for it.
 *
 * Travel-only orders have no non-travel items, so they get no retry and are
 * asked to rebook, which is the rule we want.
 */
export const hasNonTravelItems = (orderItems?: any[] | null): boolean =>
  Array.isArray(orderItems) &&
  orderItems.some(
    (item: any) => String(item?.productType || "").toLowerCase() !== "travel",
  );
